import { camelCase, isPlainObject, map, omit, pick, toNumber } from 'lodash';
import { bianka, BIANKA_ACTIVITY_TYPES } from '@fiverr-private/obs';
import { Impression, Impression_Component } from '@fiverr-private/bianka_js';
import { ListingComponent } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/listing_component';
import { Sorter } from '@fiverr-private/bianka_js/protobuf/bianka/enums/listing/sorter';
import { Context } from '@fiverr-private/bianka_js/protobuf/bianka/enums/listing/context';
import { FiverrContext } from '@fiverr-private/bianka_js/protobuf/bianka/enums/listing/fiverr_context';
import { ViewType } from '@fiverr-private/bianka_js/protobuf/bianka/enums/listing/view_type';
import { NeoDefaultState } from '@fiverr-private/bianka_js/protobuf/bianka/enums/listing/neo_default_state';
import { SellerLevel } from '@fiverr-private/bianka_js/protobuf/bianka/enums/listing/seller_level';
import { Badge } from '@fiverr-private/bianka_js/protobuf/bianka/enums/gig/badge';
import { Engine } from '@fiverr-private/bianka_js/protobuf/bianka/enums/gig/engine';
import { GigCardComponent } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/gig_card_component';
import type { GigListingsProps } from '../../types';
import { GIG_LISTINGS_ENGINES } from '../../constants';

const deepMapKeys = (obj = {}, mapFunc = (key: string) => key) => {
    const result = {};

    Object.entries(obj).forEach(([key, value]) => {
        const mappedKey = mapFunc(key);

        if (isPlainObject(value)) {
            result[mappedKey] = deepMapKeys(value as object, mapFunc);
        } else {
            result[mappedKey] = value;
        }
    });

    return result;
};

const onImpression = <T extends object = object>(impressionData: Impression_Component, message: T, type: any) => {
    bianka.reportActivity<Impression, T>({
        type: BIANKA_ACTIVITY_TYPES.IMPRESSION,
        event: {
            type: 'builder_listings_impression',
            component: deepMapKeys(impressionData, camelCase) as Impression_Component,
        },
        ...(message && {
            additionalData: {
                message: deepMapKeys(message, camelCase) as T,
                type,
            },
        }),
    });
};

const getContext = (engine: GigListingsProps['engine']): Context => {
    switch (engine) {
        case GIG_LISTINGS_ENGINES.search: {
            return Context.SEARCH;
        }
        case GIG_LISTINGS_ENGINES.categories: {
            return Context.SUBCATEGORY;
        }
        default: {
            return Context.UNSPECIFIED;
        }
    }
};

const convertSellerLevel = (sellerLevel: string): SellerLevel => SellerLevel[sellerLevel?.replace('SELLER_LEVEL_', '')];
const convertBadge = (badge: string): Badge => Badge[badge?.replace('BADGE_', '')];
const convertEngine = (engine: string): Engine => Engine[engine?.replace('ENGINE_', '')];

const getActiveExperiments = (activeExperiments = {}) =>
    Object.entries(activeExperiments).map(([id, group]) => ({ id: toNumber(id), group: toNumber(group) }));

export const shouldSendImpressions = ({ engine }: Pick<GigListingsProps, 'engine'>) =>
    engine === GIG_LISTINGS_ENGINES.categories || engine === GIG_LISTINGS_ENGINES.search;

export const getListingsImpressionEnrichment = ({
    gigs = [],
    engine,
    activeExperiments,
    categoryId,
    subcategoryId,
    nestedSubcategoryId,
    dominantLeafCategory,
    significantLeafCategories = [],
    searchQuery,
}: GigListingsProps): ListingComponent & Pick<Impression_Component, 'name'> => ({
    name: 'builder_listings_page',
    listingAttributes: {
        resultsNumber: gigs.length,
        pageNum: 1,
        sorter: Sorter.RECOMMENDED,
        context: getContext(engine),
        fiverrContext: FiverrContext.DEFAULT,
        viewType: ViewType.GRID,
        togglesList: [],
        activeFilters: [],
        lastlyAppliedFilters: [],
        activeExperiments: getActiveExperiments(activeExperiments),
        contextAttributes: {
            ...(engine === GIG_LISTINGS_ENGINES.categories
                ? {
                      oneofKind: 'categoryAttributes',
                      categoryAttributes: {
                          categoryId: toNumber(categoryId),
                          subcategoryId: toNumber(subcategoryId),
                          ...(nestedSubcategoryId && { nestedSubcategoryId: toNumber(nestedSubcategoryId) }),
                          leafCategoryId: toNumber(nestedSubcategoryId) || toNumber(subcategoryId),
                      },
                  }
                : {
                      oneofKind: 'searchAttributes',
                      searchAttributes: {
                          searchQuery: searchQuery as string,
                          dominantLeafCategory: toNumber(dominantLeafCategory),
                          significantLeafCategories: map(significantLeafCategories, toNumber) || [],
                      },
                  }),
        },
        neoDefaultState: NeoDefaultState.UNSPECIFIED,
        listingId: '',
        createdByNeo: false,
    },
});

export const onListingsContainerImpression = (impressionData: Impression_Component, { engine }: GigListingsProps) => {
    if (!shouldSendImpressions({ engine })) {
        return;
    }

    const listingAttributesKey = 'listingAttributes';

    onImpression<ListingComponent>(
        omit(impressionData, listingAttributesKey),
        pick(impressionData, listingAttributesKey) as ListingComponent,
        ListingComponent
    );
};

export const onGigCardImpression = (impressionData: Impression_Component, { engine }: GigListingsProps) => {
    if (!shouldSendImpressions({ engine })) {
        return;
    }

    const gigKey = 'gig_card';
    const gigData = pick(impressionData, gigKey)[gigKey];

    gigData.seller.seller_level = convertSellerLevel(gigData.seller.seller_level);
    gigData.gig_card_attributes.badges = map(gigData.gig_card_attributes.badges, convertBadge);
    gigData.gig_card_attributes.engine = convertEngine(gigData.gig_card_attributes.engine);

    onImpression<GigCardComponent>(omit(impressionData, gigKey), gigData, GigCardComponent);
};
